/* Copyright (c) 2017 Backpack Health LLC */

/* eslint-disable camelcase */
/* global window */

import { apiRequest, authorizedApiRequest } from './apiRequest';
import { localstore, authToken, sessionstore } from 'lib/storagePolyfill';
import AppUtils from 'lib/AppUtils';
import ApiErrorModel from 'models/ApiErrorModel';
import AccessTokenModel from 'models/AccessTokenModel';

const api = {
  create: function createSession(credentials) {
    const encodedCredentials = window.btoa(
      `${credentials.email}:${credentials.password}`
    );

    return apiRequest('post', 'sessions', null, {
      Authorization: `Basic ${encodedCredentials}`,
    });
  },
  verifyCredentials: function verifyCredentials(credentials) {
    const encodedCredentials = window.btoa(
      `${credentials.email}:${credentials.password}`
    );

    return apiRequest('head', 'sessions', null, {
      Authorization: `Basic ${encodedCredentials}`,
    });
  },
  mfaLogin: function createSession() {
    return authorizedApiRequest('post', 'sessions/mfa/login');
  },
  get: function getSession(token) {
    return apiRequest('get', `sessions/${token}`);
  },
  del: function deleteSession(token) {
    return apiRequest('delete', `sessions/${token}`);
  },
  refresh: function refreshToken() {
    return authorizedApiRequest('post', 'sessions/refresh');
  },
  logout: function logout() {
    const token = authToken.get();
    authToken.remove();
    return this.del(token);
  },
  sendMfaCode(token) {
    return apiRequest('post', 'sessions/mfa/code', null, {
      Authorization: `Bearer ${token}`,
    });
  },
  enableMfa() {
    return authorizedApiRequest('put', 'sessions/mfa/enable');
  },
  mfaForgotPassword(token) {
    return apiRequest('post', 'sessions/mfa/forgotPassword', null, {
      Authorization: `Bearer ${token}`,
    });
  },
  changePassword(credentials) {
    const encodedCredentials = window.btoa(
      `${credentials.email}:${credentials.password}`
    );
    return apiRequest('post', 'sessions/changePassword', null, {
      Authorization: `Basic ${encodedCredentials}`,
    });
  },
  mfaChangePassword(credentials) {
    const encodedCredentials = window.btoa(
      `${credentials.email}:${credentials.password}`
    );
    return apiRequest('post', 'sessions/mfa/changePassword', null, {
      Authorization: `Basic ${encodedCredentials}`,
    });
  },
  changeEmail(token) {
    return apiRequest('post', 'sessions/changeEmail', null, {
      Authorization: `Bearer ${token}`,
    });
  },
  mfaChangeEmail(token) {
    return apiRequest('post', 'sessions/mfa/changeEmail', null, {
      Authorization: `Bearer ${token}`,
    });
  },
};

const sessionApi = {
  login(credentialsData) {
    return api.create({
      email: credentialsData.email,
      password: credentialsData.password,
    })({
      duration: credentialsData.duration,
      group_id: credentialsData.groupId,
    })
    .then((res) => {
      AppUtils.clearSavedData({ retainMetrics: true });
      authToken.init(
        credentialsData.duration,
        `${res.data.data.access_token}`
      );
      return AccessTokenModel.extractDataFromAuthToken(res.data.data.access_token);
    });
  },

  verifyCredentials({ email, password }) {
    return api.verifyCredentials({ email, password })()
    .then(res => null);
  },

  mfaLogin(mfaCode, duration) {
    return api.mfaLogin()({
      mfa_code: mfaCode,
      duration,
    })
    .then(res => {
      AppUtils.clearSavedData();
      authToken.init('', `${res.data.data.access_token}`);
      return AccessTokenModel.extractDataFromAuthToken(res.data.data.access_token);
    });
  },

  mfaForgotPassword(token, password, mfaCode, duration) {
    return api.mfaForgotPassword(token)({
      mfa_code: mfaCode,
      password,
      duration,
    })
    .then(res => {
      AppUtils.clearSavedData();
      authToken.init('', `${res.data.data.access_token}`);
      return AccessTokenModel.extractDataFromAuthToken(res.data.data.access_token);
    });
  },

  get(token) {
    return api.get(token)()
    .then(res => res.data.data);
  },

  refresh() {
    return api.refresh()()
    .then(res => {
      authToken.set(res.data.data.access_token);
      return AccessTokenModel.extractDataFromAuthToken(res.data.data.access_token);
    })
    .catch(err => {
      return Promise.reject(err);
    });
  },

  logout() {
    if (!authToken.get()) { return Promise.resolve(); }

    return api.logout()()
    .then(res => null);
  },

  sendMfaCode(token) {
    return api.sendMfaCode(token)()
    .then(res => res.data.data);
  },

  enableMfa(mfaCode) {
    return api.enableMfa()({
      mfa_code: mfaCode,
    })
    .then(res => null);
  },

  changePassword(credentials, password, duration) {
    return api.changePassword(credentials)({
      password,
      duration,
    })
    .then(res => {
      authToken.init(duration, `${res.data.data.access_token}`);
      return null;
    });
  },

  mfaChangePassword(credentials, mfa_code, password, duration) {
    return api.mfaChangePassword(credentials)({
      mfa_code,
      password,
      duration,
    })
    .then(res => {
      AppUtils.clearSavedData();
      authToken.init(duration, `${res.data.data.access_token}`);
      return null;
    });
  },

  changeEmail(token, password, duration) {
    return api.changeEmail(token)({
      password,
      duration,
    })
    .then(res => {
      AppUtils.clearSavedData();
      authToken.init(duration, `${res.data.data.access_token}`);
      return null;
    });
  },

  mfaChangeEmail(token, password, mfa_code, duration) {
    return api.mfaChangeEmail(token)({
      mfa_code,
      password,
      duration,
    })
    .then(res => {
      AppUtils.clearSavedData();
      authToken.init(duration, `${res.data.data.access_token}`);
      return null;
    });
  },
};

export default sessionApi;
